import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';

import { navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import BrandCard from 'components/brand-card/BrandCard';
import CroppedProfileImage from 'components/cropped-profile-image/CroppedProfileImage';
import Pagination from 'components/pagination/Pagination';
import { withModal } from 'components/modal/ModalHOC';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { fetchAgencyBrandsProfiles, resetBrandProfiles } from '../reducers/agencyBrandProfiles';
import { fetchBrandProfile, resetBrandProfile } from 'modules/brands/reducers/brandProfile';
import { fetchCurrentUserOrganizations, selectOrganizationById } from 'modules/organizations/reducers/organizations';
import BrandCreateProfileModal from 'modules/brands/components/brand-create-profile-modal/BrandCreateProfileModal';

import { updateQueryParams } from 'utils/location';
import { __t, applyTranslations } from 'utils/language';

import type {
  BrandProfileAgency,
  Data,
  Image,
  LocationWindow,
  MixedSelectDropdownItem,
  Pagination as PaginationType
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./agency-overview.i18n.json'));

const BrandCreateProfileButton = withModal(ActionButton, {
  modalContent: BrandCreateProfileModal,
  width: 'auto'
});

type StateProps = {
  location: LocationWindow,
  agencyImage: Image | undefined | null,
  agencyName: string,
  organizationsListIds: Array<number>,
  brandProfilesList: Array<BrandProfileAgency>,
  brandProfilesPagination: PaginationType
};

type OwnProps = {
  agencyProfileId: number,
  agencyBrandProfileId: number
};

type DispatchProps = {
  dispatchFetchBrandProfile: (a: number) => Promise<void>,
  dispatchResetBrandProfile: () => Promise<void>,
  dispatchFetchAgencyBrandsProfile: (b: number, a?: any | null) => Promise<void>,
  dispatchResetBrandProfiles: () => Promise<void>,
  dispatchFetchCurrentUserOrganizations: () => Promise<void>,
  dispatchSelectOrganizationById: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type QueryParams = {
  sort: string,
  page: number
};

const SORT_OPTIONS = [{
  label: __t('agency_overview.sort.recently_selected_desc'),
  value: 'recently_selected.desc'
}, {
  label: __t('agency_overview.sort.created_at_desc'),
  value: 'created_at.desc'
}, {
  label: __t('agency_overview.sort.name_asc'),
  value: 'name.asc'
}, {
  label: __t('agency_overview.sort.num_planned_campaigns_desc'),
  value: 'num_planned_campaigns.desc'
}, {
  label: __t('agency_overview.sort.num_ongoing_campaigns_desc'),
  value: 'num_ongoing_campaigns.desc'
}];

const DEFAULT_QUERY_PARAMS = {
  sort: SORT_OPTIONS[0].value,
  page: 1
};

class AgencyOverview extends PureComponent<Props> {
  componentDidMount() {
    const {
      agencyBrandProfileId,
      dispatchFetchBrandProfile
    } = this.props;

    if (agencyBrandProfileId) {
      dispatchFetchBrandProfile(agencyBrandProfileId);
    }

    this.fetchData();
    addUserAction({
      eventCategory: 'drop down',
      eventAction: 'navigate',
      stage: 'finish',
      destination: 'agency-settings'
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(this.getValuesFromUrl(), this.getValuesFromUrl(prevProps))) {
      this.fetchData();
    }
  }

  componentWillUnmount() {
    const { dispatchResetBrandProfile, dispatchResetBrandProfiles } = this.props;

    dispatchResetBrandProfile();
    dispatchResetBrandProfiles();
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { sort } = this.getValuesFromUrl();

    return (
      SORT_OPTIONS.map(item => ({
        identifier: item.value,
        isDisabled: false,
        isSelected: sort === item.value,
        label: item.label,
        value: item.value
      }))
    );
  };

  handleClose = () => {
    const { dispatchFetchCurrentUserOrganizations } = this.props;

    this.fetchData();

    dispatchFetchCurrentUserOrganizations();
  };

  fetchData(): void {
    const { agencyProfileId, dispatchFetchAgencyBrandsProfile } = this.props;
    const { page, sort } = this.getValuesFromUrl();

    dispatchFetchAgencyBrandsProfile(agencyProfileId, {
      itemsPerPage: 4,
      page,
      sort
    });
  }

  getValuesFromUrl(props: Props = this.props): QueryParams {
    const { location: { query } } = props;
    const isSortValueValid = !query.sort || SORT_OPTIONS.map(v => v.value).includes(query.sort);
    const params: typeof DEFAULT_QUERY_PARAMS = {
      sort: isSortValueValid ? query.sort : DEFAULT_QUERY_PARAMS.sort,
      page: query.page
    };

    return { ...DEFAULT_QUERY_PARAMS, ...params };
  }

  isDisabled(brandProfile: BrandProfileAgency): boolean {
    const { organizationsListIds } = this.props;
    if (!organizationsListIds) {
      return false;
    }

    return brandProfile && (organizationsListIds.indexOf(brandProfile.organizationId) === -1);
  }

  handleViewSettingsPage = (e: SyntheticEvent<HTMLDivElement>): void => {
    e.stopPropagation();

    const { dispatchSelectOrganizationById } = this.props;
    const organizationId = Number(e.currentTarget.dataset.arg);

    dispatchSelectOrganizationById(organizationId);
    navigate.to(`/organizations/${organizationId}/settings/team-members`);
  };

  handleViewCampaignsPage = (e: SyntheticEvent<HTMLDivElement>): void => {
    e.stopPropagation();

    const { dispatchSelectOrganizationById } = this.props;
    const organizationId = Number(e.currentTarget.dataset.arg);

    dispatchSelectOrganizationById(organizationId);
    navigate.to(`/organizations/${organizationId}/campaigns`);
  };

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { page });
  };

  handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      page: 1,
      sort: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  render() {
    const { agencyImage, brandProfilesList, brandProfilesPagination, location, agencyName, agencyProfileId } = this.props;
    const { sort } = this.getValuesFromUrl();
    const dropdownItems = this.getDropdownItems();

    return (
      <section className="agency-overview">
        <h1 className="agency-overview__name">
          {agencyName}
        </h1>
        <div className="agency-overview__image">
          <CroppedProfileImage
            type="cover-medium"
            canEdit={false}
            image={agencyImage}
          />
        </div>
        <div className="agency-overview-actions">
          <label
            htmlFor="agency_sort"
            className="agency-overview-actions__sort-label"
          >
            {__t('agency_overview.label')}:
          </label>
          <MixedSelectDropdown
            key={`agency-overview-actions__dropdown--${sort}`}
            name="agency-overview-actions__dropdown"
            items={dropdownItems}
            withSelectedInList
            className="agency-overview-actions__dropdown"
            onItemsToggle={this.handleSortChange}
          />
          <div className="agency-overview__controls">
            <BrandCreateProfileButton
              color="primary"
              modalContentProps={{
                agencyProfileId,
                onAfterCloseModal: this.handleClose
              }}
            >
              {__t('agency_overview.create_button')}
            </BrandCreateProfileButton>
          </div>
        </div>
        <div className="agency-overview__brands">
          {
            brandProfilesList.map(brand => (
              <BrandCard
                key={brand.id}
                brand={brand}
                isDisabled={this.isDisabled(brand)}
                onMembersClick={this.handleViewSettingsPage}
                onCampaignClick={this.handleViewCampaignsPage}
              />
            ))
          }
        </div>
        {
          brandProfilesPagination && (
            <Pagination
              className="agency-overview__pagination"
              location={location}
              meta={brandProfilesPagination}
              links={brandProfilesPagination.links}
              onClick={this.handlePageChange}
            />
          )
        }
      </section>
    );
  }
}
const mapStateToProps = ({ agencyBrandProfiles, brandProfile, organizations, router }): StateProps => ({
  location: router.location,
  agencyImage: brandProfile.profile.coverImage,
  agencyName: brandProfile.profile.name,
  organizationsListIds: organizations.list.map(organization => organization.id),
  brandProfilesList: agencyBrandProfiles.list,
  brandProfilesPagination: agencyBrandProfiles.pagination
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCurrentUserOrganizations: () => dispatch(fetchCurrentUserOrganizations()),
  dispatchFetchBrandProfile: profileId => dispatch(fetchBrandProfile(profileId)),
  dispatchResetBrandProfile: () => dispatch(resetBrandProfile()),
  dispatchFetchAgencyBrandsProfile: (agencyProfileId, query) => dispatch(fetchAgencyBrandsProfiles(agencyProfileId, query)),
  dispatchResetBrandProfiles: () => dispatch(resetBrandProfiles()),
  dispatchSelectOrganizationById: organizationId => dispatch(selectOrganizationById(organizationId))
});

export default connect(mapStateToProps, mapDispatchToProps)(AgencyOverview);
